import type { ReactNode } from 'react'
import type { DeepKeys, DeepValue } from '@tanstack/react-form'
import type { OrganizationId } from '@/lib/organization-scoping'
import type {
  SuperTicketAdjustments,
  SuperTicketLayout,
  SuperTicketPreview as SuperTicketPreviewPayload,
  SuperTicketTemplateSettings,
} from '@/lib/services/super-ticket-settings/super-ticket-settings.types'
import type { getSuperTicketSectionOverflow } from '@/lib/super-ticket-overflow'
import { useEffect, useRef, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  RotateCcw,
  Printer,
  SlidersHorizontal,
} from 'lucide-react'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Slider } from '@/components/ui/slider'
import { Textarea } from '@/components/ui/textarea'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { submitSchemaValidator, useAppForm } from '@/lib/form'
import { getErrorMessage } from '@/lib/utils'
import { superTicketQueries } from '@/lib/services/super-ticket-settings/super-ticket-settings.query'
import {
  nudgeSuperTicket,
  editableSuperTicketLayout,
  superTicketLayoutSchema,
} from '@/lib/services/super-ticket-settings/super-ticket-settings.schema'
import { SuperTicketPreview } from './SuperTicketPreview'

// The API's SuperTicketLayout::sections keys are finite; plain string keys can also
// contain dots, which makes TanStack's deep paths ambiguous with whole-section writes.
const editorSectionKeys = {
  'sender-card': true,
  'recipient-card': true,
  'online-card': true,
  'care-card': true,
  'enclosure-card': true,
  'handmade-card': true,
  'shop-card': true,
  'homepage-qr-card': true,
  'fulfillment-label': true,
  'shop-branding': true,
  'mercury-order-summary': true,
  'mercury-products-and-totals': true,
  'florist-summary': true,
  'fulfillment-slip': true,
}
type EditorSectionKey = keyof typeof editorSectionKeys
export type SuperTicketEditorLayout = Omit<SuperTicketLayout, 'sections'> & {
  sections: Partial<Record<EditorSectionKey, SuperTicketLayout['sections'][string]>>
}
function isEditorSectionKey(value: string | undefined): value is EditorSectionKey {
  return value !== undefined && Object.hasOwn(editorSectionKeys, value)
}

type LoadedPreview = { sections: ReturnType<typeof getSuperTicketSectionOverflow>; html: string }
type FieldChange = <Name extends DeepKeys<SuperTicketEditorLayout>>(
  name: Name,
  value: DeepValue<SuperTicketEditorLayout, Name>
) => void
type Toolbar = ReactNode | ((state: { isSubmitting?: boolean }) => ReactNode)
type EditorSharedProps = {
  organizationId: OrganizationId
  metadata: SuperTicketTemplateSettings
  resetLayout: SuperTicketEditorLayout
  salesOrderId?: number | string | null
  submitLabel?: string
  oneOff?: boolean
  isReadOnly?: boolean
  onOrderSummary?: () => unknown
  onCancel?: () => void
  toolbar?: Toolbar
  footer?: ReactNode
}
type SuperTicketEditorProps = EditorSharedProps & {
  initialLayout: SuperTicketLayout
  onSubmit: (preview: SuperTicketPreviewPayload) => unknown
}
type SuperTicketLayoutEditorProps = EditorSharedProps & {
  onFieldChange: FieldChange
  onReset: () => void
  layout: SuperTicketEditorLayout
  isSubmitting?: boolean
  error?: string | null
  onPreviewReady?: (sections: LoadedPreview['sections'], html: string) => void
  onPrintTest?: (preview: SuperTicketPreviewPayload) => unknown
  initialSection?: string
  onSectionChange?: (section: string) => void
  showSubmit?: boolean
}

export function SuperTicketEditor({
  organizationId,
  metadata,
  initialLayout,
  resetLayout,
  salesOrderId,
  onSubmit,
  submitLabel = 'Save shop defaults',
  oneOff = false,
  isReadOnly = false,
  onOrderSummary,
  onCancel,
  toolbar,
  footer,
}: SuperTicketEditorProps) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null | undefined>(null)
  const [loadedPreview, setLoadedPreview] = useState<LoadedPreview | null>(null)
  const [initial] = useState<SuperTicketEditorLayout>(() =>
    editableSuperTicketLayout(initialLayout)
  )
  const [baseline] = useState(() => editableSuperTicketLayout(resetLayout))
  const submitting = useRef(false)
  const active = useRef(true)
  // A pending preview must not print after its dialog or order boundary unmounts.
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  const form = useAppForm({
    defaultValues: initial,
    validators: { onSubmit: submitSchemaValidator(superTicketLayoutSchema(metadata)) },
    onSubmit: async ({ value }) => {
      if (submitting.current || isReadOnly) return
      submitting.current = true
      setError(null)
      try {
        // Print the HTML already previewed for this exact order and layout.
        // Avoid a second network round trip that can expire browser popup activation.
        const preview = queryClient.getQueryData(
          superTicketQueries.preview({
            organizationId,
            template: metadata.template,
            layout: value,
            salesOrderId,
          }).queryKey
        )
        if (!preview || loadedPreview?.html !== preview.html)
          throw new Error('Wait for the print preview to finish loading.')
        if (active.current) {
          const submitted = await onSubmit(preview)
          if (oneOff && submitted === true && active.current) onCancel?.()
        }
      } catch (error) {
        setError(getErrorMessage(error))
      } finally {
        submitting.current = false
      }
    },
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        event.stopPropagation()
        void form.handleSubmit()
      }}
    >
      <form.Subscribe
        selector={(state) =>
          [state.values, state.isSubmitting] satisfies [SuperTicketEditorLayout, boolean]
        }
      >
        {([layout, isSubmitting]) => (
          <SuperTicketLayoutEditor
            onFieldChange={(name, value) => form.setFieldValue(name, value)}
            onReset={() => form.reset(structuredClone(baseline), { keepDefaultValues: true })}
            layout={layout}
            metadata={metadata}
            organizationId={organizationId}
            salesOrderId={salesOrderId}
            resetLayout={baseline}
            oneOff={oneOff}
            isReadOnly={isReadOnly}
            isSubmitting={isSubmitting}
            submitLabel={submitLabel}
            onOrderSummary={onOrderSummary}
            onCancel={onCancel}
            error={error}
            toolbar={toolbar}
            footer={footer}
            onPreviewReady={(sections, html) => setLoadedPreview({ sections, html })}
          />
        )}
      </form.Subscribe>
    </form>
  )
}

export function SuperTicketLayoutEditor({
  onFieldChange,
  onReset,
  layout,
  metadata,
  organizationId,
  salesOrderId,
  resetLayout,
  oneOff,
  isReadOnly,
  isSubmitting,
  submitLabel,
  onOrderSummary,
  onCancel,
  error,
  toolbar,
  footer,
  onPreviewReady,
  onPrintTest,
  initialSection,
  onSectionChange,
  showSubmit = true,
}: SuperTicketLayoutEditorProps) {
  const [loadedPreview, setLoadedPreview] = useState<LoadedPreview | null>(null)
  const [customizing, setCustomizing] = useState(!oneOff)
  const [selectedSection, setSelectedSection] = useState(
    initialSection ?? Object.keys(metadata.sections)[0]
  )
  const [selectedTarget, setSelectedTarget] = useState<'text' | 'logo'>('text')
  const [previewDelay, setPreviewDelay] = useState(300)
  const changeField = <Name extends DeepKeys<SuperTicketEditorLayout>>(
    field: Name,
    value: DeepValue<SuperTicketEditorLayout, Name>,
    delay = 300
  ) => {
    setPreviewDelay(delay)
    onFieldChange(field, value)
  }
  const selectSection = (key: string) => {
    setSelectedSection(key)
    onSectionChange?.(key)
    setSelectedTarget('text')
  }
  // Keep controls immediate, but batch a burst of edits into one preview request.
  // Changing the query key also cancels its previous HTTP request through Query's signal.
  const previewLayout = useDebounce(layout, previewDelay)
  const editsPending = previewLayout !== layout
  const preview = useQuery({
    ...superTicketQueries.preview({
      organizationId,
      template: metadata.template,
      layout: previewLayout,
      salesOrderId,
    }),
    // Keep the sheet's geometry stable while nudges generate the next preview.
    placeholderData: keepPreviousData,
  })
  const overflowing = loadedPreview?.sections ?? []
  const workTicketOverflows = overflowing.some((key) =>
    [
      'florist-summary',
      'mercury-order-summary',
      'mercury-products-and-totals',
      'fulfillment-slip',
    ].some((section) => section === key)
  )
  const overflowLabels = overflowing
    .map((key) => (key === undefined ? key : (metadata.sections[key]?.label ?? key)))
    .join(', ')
  const previewReady =
    !editsPending &&
    loadedPreview !== null &&
    loadedPreview.html === preview.data?.html &&
    !preview.isPlaceholderData
  const section = selectedSection === undefined ? undefined : metadata.sections[selectedSection]
  const values = isEditorSectionKey(selectedSection) ? layout.sections[selectedSection] : undefined
  const resetValues = isEditorSectionKey(selectedSection)
    ? resetLayout.sections[selectedSection]
    : undefined
  if (!isEditorSectionKey(selectedSection) || !section || !values || !resetValues) return null
  const hasLogoTarget = Boolean(section.targets?.logo && values.fields.logo)
  const target = hasLogoTarget ? selectedTarget : 'text'
  const targetName = section.targets?.[target]?.toLowerCase() ?? 'section'
  const adjustments = target === 'logo' ? values.logo : values
  const resetAdjustments = target === 'logo' ? resetValues.logo : resetValues
  if (!adjustments || !resetAdjustments) return null
  const disabled = isReadOnly || isSubmitting
  const direction = layout.rotation === 180 ? -1 : 1
  const offset = { x: adjustments.x * direction, y: adjustments.y * direction }
  const setAdjustment = (key: keyof SuperTicketAdjustments, value: number, delay?: number) =>
    changeField(
      target === 'logo'
        ? `sections.${selectedSection}.logo.${key}`
        : `sections.${selectedSection}.${key}`,
      value,
      delay
    )
  const nudge = (axis: 'x' | 'y', delta: number) =>
    setAdjustment(
      axis,
      nudgeSuperTicket(adjustments[axis], delta * direction, metadata.limits.offset),
      200
    )

  return (
    <div className="@container/ticket-editor space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {typeof toolbar === 'function' ? toolbar({ isSubmitting }) : toolbar}
        {!oneOff ? (
          <Typography variant="description">
            Saved defaults apply wherever your shop prints this paper template.
          </Typography>
        ) : null}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {customizing ? (
            <div className="flex items-center gap-2">
              <Label htmlFor="ticket-orientation">Orientation</Label>
              <Select
                value={String(layout.rotation)}
                onValueChange={(value) => {
                  if (value === '0' || value === '180')
                    changeField('rotation', value === '180' ? 180 : 0)
                }}
                disabled={disabled}
              >
                <SelectTrigger id="ticket-orientation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Normal</SelectItem>
                  <SelectItem value="180">Rotate 180°</SelectItem>
                </SelectContent>
              </Select>
            </div>
          ) : null}
          {oneOff ? (
            <Button
              type="button"
              variant="link"
              size="inline"
              onClick={() => setCustomizing(!customizing)}
            >
              <SlidersHorizontal className="size-4" />
              {customizing ? 'Hide adjustments' : 'Customize print'}
            </Button>
          ) : null}
        </div>
      </div>
      <div
        className={
          customizing
            ? 'grid min-w-0 items-start gap-3 @2xl/ticket-editor:grid-cols-[minmax(0,1fr)_16rem]'
            : 'min-w-0'
        }
      >
        <div className="min-w-0 space-y-3">
          <Card size="flush">
            <CardContent className="p-3">
              {preview.isPending ? (
                <Typography role="status" variant="description">
                  Loading print preview…
                </Typography>
              ) : preview.isLoadingError ? (
                <Alert variant="destructive">
                  <AlertTitle>The print preview could not be loaded.</AlertTitle>
                  <AlertDescription>
                    <Button
                      type="button"
                      variant="link"
                      size="inline"
                      onClick={() => preview.refetch()}
                    >
                      Try again
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : (
                <SuperTicketPreview
                  html={preview.data.html}
                  page={metadata.page}
                  sections={metadata.sections}
                  selectedSection={customizing ? selectedSection : null}
                  onSelectSection={customizing ? selectSection : undefined}
                  onOverflowChange={(sections, html) => {
                    setLoadedPreview({ sections, html })
                    onPreviewReady?.(sections, html)
                  }}
                />
              )}
            </CardContent>
          </Card>
          <Typography variant="caption" tone="muted">
            {metadata.page.width} × {metadata.page.height} inches ·{' '}
            {layout.rotation === 180 ? 'Rotated 180°' : 'Normal orientation'}
            {!preview.isPending && (preview.isFetching || !previewReady)
              ? ' · Updating preview…'
              : ''}
          </Typography>
          {overflowing.length ? (
            <Alert>
              <AlertTitle>
                {workTicketOverflows
                  ? 'This order doesn’t fit on the work ticket'
                  : 'Content extends beyond its section'}
              </AlertTitle>
              <AlertDescription>
                <Typography variant="description">
                  {overflowLabels}: there is more content than the available space on this page.{' '}
                  {workTicketOverflows
                    ? 'Consider printing an Order Summary instead to include all order details.'
                    : 'Reduce the content size or adjust its position to fit inside the section.'}
                </Typography>
                {workTicketOverflows && onOrderSummary ? (
                  <Button type="button" variant="link" size="inline" onClick={onOrderSummary}>
                    Print Order Summary
                  </Button>
                ) : null}
              </AlertDescription>
            </Alert>
          ) : null}
        </div>
        {customizing ? (
          <Card size="flush">
            <CardContent className="space-y-4 p-4">
              <div className="space-y-2">
                <Label htmlFor="ticket-section">Section to adjust</Label>
                <Select value={selectedSection} onValueChange={selectSection}>
                  <SelectTrigger id="ticket-section">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(metadata.sections).map(([key, section]) => (
                      <SelectItem key={key} value={key}>
                        {section.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Typography variant="caption" tone="muted">
                  Or click a section in the preview.
                </Typography>
              </div>
              <fieldset disabled={disabled} className="space-y-4">
                {hasLogoTarget ? (
                  <div className="space-y-2">
                    <Label htmlFor="ticket-target">Adjust</Label>
                    <Select
                      value={target}
                      onValueChange={(value) => {
                        if (value === 'text' || value === 'logo') setSelectedTarget(value)
                      }}
                      disabled={disabled}
                    >
                      <SelectTrigger id="ticket-target">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(section.targets ?? {}).map(([key, label]) => (
                          <SelectItem key={key} value={key}>
                            {label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
                <div className="space-y-2">
                  <Typography variant="label">Position</Typography>
                  <div className="flex flex-col items-center gap-2">
                    <div className="grid shrink-0 grid-cols-3 gap-1">
                      <span />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Move ${targetName} up`}
                        onClick={() => nudge('y', -1)}
                        disabled={disabled || offset.y <= metadata.limits.offset.min}
                      >
                        <ArrowUp />
                      </Button>
                      <span />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Move ${targetName} left`}
                        onClick={() => nudge('x', -1)}
                        disabled={disabled || offset.x <= metadata.limits.offset.min}
                      >
                        <ArrowLeft />
                      </Button>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            type="button"
                            variant="outline"
                            size="icon-sm"
                            aria-label={`Reset ${targetName} position`}
                            onClick={() => {
                              setAdjustment('x', resetAdjustments.x)
                              setAdjustment('y', resetAdjustments.y)
                            }}
                            disabled={disabled}
                          >
                            <RotateCcw />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Reset position</TooltipContent>
                      </Tooltip>
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Move ${targetName} right`}
                        onClick={() => nudge('x', 1)}
                        disabled={disabled || offset.x >= metadata.limits.offset.max}
                      >
                        <ArrowRight />
                      </Button>
                      <span />
                      <Button
                        type="button"
                        variant="outline"
                        size="icon-sm"
                        aria-label={`Move ${targetName} down`}
                        onClick={() => nudge('y', 1)}
                        disabled={disabled || offset.y >= metadata.limits.offset.max}
                      >
                        <ArrowDown />
                      </Button>
                    </div>
                    <Typography variant="caption" tone="muted" align="center" aria-live="polite">
                      <span className="block whitespace-nowrap">
                        Horizontal {offset.x.toFixed(2)} in
                      </span>
                      <span className="block whitespace-nowrap">
                        Vertical {offset.y.toFixed(2)} in
                      </span>
                    </Typography>
                  </div>
                </div>
                <div className="space-y-3">
                  <Typography variant="label">Content size · {adjustments.scale}%</Typography>
                  <Slider
                    aria-label={`${targetName === 'section' ? 'Section' : section.targets?.[target]} content size`}
                    value={[adjustments.scale]}
                    {...metadata.limits.scale}
                    disabled={disabled}
                    onValueChange={([value]) => {
                      if (value !== undefined) setAdjustment('scale', value)
                    }}
                    thumbAriaValueText={`${adjustments.scale}%`}
                  />
                </div>
                <div className="space-y-3">
                  <Typography variant="label">Include in this section</Typography>
                  {Object.entries(section.fields).map(([key, label]) => (
                    <div key={key} className="flex items-center gap-2">
                      <Checkbox
                        id={`ticket-field-${key}`}
                        checked={values.fields[key]}
                        disabled={disabled}
                        onCheckedChange={(checked) => {
                          if (key === 'logo' && checked !== true) setSelectedTarget('text')
                          changeField(`sections.${selectedSection}.fields.${key}`, checked === true)
                        }}
                      />
                      <Label htmlFor={`ticket-field-${key}`}>{label}</Label>
                    </div>
                  ))}
                  {section.custom_text && values.fields.custom_text ? (
                    <div className="space-y-2">
                      <Label htmlFor="ticket-custom-text">Text to print</Label>
                      <Textarea
                        id="ticket-custom-text"
                        value={values.custom_text}
                        maxLength={section.custom_text.max_length}
                        rows={3}
                        disabled={disabled}
                        onChange={(event) =>
                          changeField(`sections.${selectedSection}.custom_text`, event.target.value)
                        }
                        aria-describedby="ticket-custom-text-count"
                      />
                      <Typography id="ticket-custom-text-count" variant="caption" tone="muted">
                        {values.custom_text?.length} / {section.custom_text.max_length} characters
                      </Typography>
                    </div>
                  ) : null}
                </div>
                <Button
                  type="button"
                  variant="link"
                  size="inline"
                  disabled={disabled}
                  onClick={() => {
                    setSelectedTarget('text')
                    changeField(`sections.${selectedSection}`, structuredClone(resetValues))
                  }}
                >
                  Reset this section
                </Button>
              </fieldset>
              {onPrintTest ? (
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    isSubmitting || preview.isFetching || preview.isLoadingError || !previewReady
                  }
                  onClick={() => {
                    if (preview.data) onPrintTest(preview.data)
                  }}
                >
                  <Printer className="size-4" />
                  Print test page
                </Button>
              ) : null}
            </CardContent>
          </Card>
        ) : null}
      </div>
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {customizing ? (
            <Button
              type="button"
              variant="link"
              size="inline"
              disabled={disabled}
              onClick={() => {
                setPreviewDelay(300)
                onReset()
              }}
            >
              {oneOff ? 'Reset to shop defaults' : 'Reset to default layout'}
            </Button>
          ) : null}
          {oneOff && customizing ? (
            <Typography variant="caption">Adjustments apply to this print only.</Typography>
          ) : null}
        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-3">
          {footer}
          {onCancel ? (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancel
            </Button>
          ) : null}
          {showSubmit && !isReadOnly ? (
            <Button
              type="submit"
              disabled={
                isSubmitting || preview.isFetching || preview.isLoadingError || !previewReady
              }
            >
              {isSubmitting ? 'Preparing…' : submitLabel}
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}
